/**
 * AgentProgress Component
 * Displays real-time progress for agent job execution
 * Automatically polls job status and shows thinking/progress
 * Supports time-based estimation when server doesn't provide percentage
 */

import React, { useState, useEffect } from 'react';
import { useAgentJob } from '../../hooks/useAgentJob';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { cn } from '@/lib/utils';
import { CheckCircle2, XCircle, Loader2, Brain } from 'lucide-react';

interface AgentProgressProps {
  /** The agent job ID to track */
  jobId: string | null | undefined;
  /** Additional CSS classes */
  className?: string;
  /** Callback when job completes successfully */
  onComplete?: (result: Record<string, any>) => void;
  /** Callback when job fails */
  onError?: (error: string) => void;
  /** Show detailed thinking text (default: true) */
  showThinking?: boolean;
  /** Compact mode with minimal UI (default: false) */
  compact?: boolean;
  /** Estimated total duration in seconds (for time-based progress) */
  estimatedDurationSeconds?: number;
  /** Number of files being analyzed (for display) */
  fileCount?: number;
}

/**
 * AgentProgress component
 * Displays agent execution progress with real-time updates
 */
export const AgentProgress: React.FC<AgentProgressProps> = ({
  jobId,
  className,
  onComplete,
  onError,
  showThinking = true,
  compact = false,
  estimatedDurationSeconds,
  fileCount,
}) => {
  const { job, loading, error } = useAgentJob(jobId, {
    onComplete,
    onError,
  });

  // Track elapsed time for time-based progress estimation
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime] = useState(() => Date.now());

  // Update elapsed time every second
  useEffect(() => {
    if (!jobId || !estimatedDurationSeconds) {
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [jobId, estimatedDurationSeconds, startTime]);

  // Don't render if no job ID
  if (!jobId) {
    return null;
  }

  // Loading state
  if (loading && !job) {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading job status...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className={className}>
        <XCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // No job data
  if (!job) {
    return null;
  }

  // Render based on job status
  const { status, progress, agentName, error: jobError } = job;

  // Completed state
  if (status === 'completed') {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-green-600', className)}>
        <CheckCircle2 className="h-4 w-4" />
        <span>
          {agentName === 'resource_analyst' ? 'Analysis complete' : 'Job completed'}
        </span>
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <Alert variant="destructive" className={className}>
        <XCircle className="h-4 w-4" />
        <AlertTitle>
          {agentName === 'resource_analyst' ? 'Analysis failed' : 'Job failed'}
        </AlertTitle>
        <AlertDescription>{jobError || 'Unknown error occurred'}</AlertDescription>
      </Alert>
    );
  }

  // Active/Pending state - show progress
  const thinking = progress.thinking;

  // Calculate progress percentage
  let percentage = progress.percentage || 0;

  // Use time-based estimation if server doesn't provide percentage and we have estimation data
  if (percentage === 0 && estimatedDurationSeconds && estimatedDurationSeconds > 0) {
    // Calculate progress based on elapsed time (cap at 95% until server confirms completion)
    const estimatedPercentage = Math.min(
      Math.floor((elapsedSeconds / estimatedDurationSeconds) * 100),
      95
    );
    percentage = estimatedPercentage;
  }

  // Compact mode - minimal UI
  if (compact) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="font-medium">
              {status === 'pending' ? 'Queued' : 'Processing'}
            </span>
          </div>
          <span className="text-muted-foreground">{percentage}%</span>
        </div>
        <Progress value={percentage} className="h-1" />
      </div>
    );
  }

  // Full mode - detailed UI
  return (
    <div className={cn('space-y-3 rounded-lg border bg-card p-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <div className="flex flex-col">
            <span className="font-medium">
              {agentName === 'resource_analyst' ? 'Analyzing Resources' : 'Processing'}
            </span>
            {fileCount && fileCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {fileCount} file{fileCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <span className="text-sm text-muted-foreground">{percentage}%</span>
      </div>

      {/* Progress bar */}
      <Progress value={percentage} className="h-2" />

      {/* Thinking text */}
      {showThinking && thinking && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <Brain className="h-3.5 w-3.5" />
            <span>Thinking</span>
          </div>
          <div className="max-h-24 overflow-y-auto rounded bg-muted/30 p-2 text-xs text-muted-foreground">
            {thinking}
          </div>
        </div>
      )}

      {/* Steps progress (if available) */}
      {progress.stepsTotal && progress.stepsTotal > 0 && (
        <div className="text-xs text-muted-foreground">
          Step {progress.stepsCompleted || 0} of {progress.stepsTotal}
        </div>
      )}
    </div>
  );
};
